import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { FloatingMenu, MENU_ITEM } from "../../components/ui/FloatingMenu";
import { OFFER_TOKEN } from "./vocabulary";
import { TOKEN_RX, VARIABLES, VARIABLE_BY_TOKEN, VariableGroup, VariableValues } from "./variables";

/**
 * Both layers share this box exactly — font, size, line height, letter spacing, padding,
 * wrapping — so the coloured text drawn behind sits under the transparent text the rep types in.
 * Letter spacing is pinned: the app's body sets -0.2px, which the backdrop inherited and the
 * textarea (a form control) didn't, so the text drifted off the caret and deleting looked broken.
 */
const BOX = "px-3 py-2 font-sans text-sm leading-6 tracking-normal whitespace-pre-wrap break-words";

const GROUPS: VariableGroup[] = ["Prospect", "Établissement", "Vous"];

/** Shown inline under the text; every other variable sits in « Toutes ». */
const QUICK = ["{firstName}", "{company}", "{location}", "{sites}", OFFER_TOKEN, "{mon prénom}"];

/**
 * A message editor that shows its variables: each `{token}` in orange italics (red when unknown);
 * hovering or clicking one shows its value — the prospect's, or an example in a template. Below, one compact line: the variables used most,
 * then « Toutes (N) », a menu of every variable at hand by group with its value; a click inserts
 * at the caret. With a prospect, the ones the base has nothing for are listed apart there.
 */
export function VariableTextarea({
    value, onChange, values, examples = false, readOnly, invalid, minRows = 5, placeholder, showList = true,
}: {
    value: string;
    onChange: (text: string) => void;
    /** token → what it becomes; null = nothing known for this prospect. */
    values: VariableValues;
    /** Template mode: the values are examples, not this prospect's. */
    examples?: boolean;
    readOnly?: boolean;
    invalid?: boolean;
    minRows?: number;
    placeholder?: string;
    showList?: boolean;
}) {
    const area = useRef<HTMLTextAreaElement>(null);
    const backdrop = useRef<HTMLDivElement>(null);
    const caret = useRef<number>(value.length);
    /** The variable hovered or clicked, and where it is drawn. */
    const [tip, setTip] = useState<{ at: number; token: string; rect: DOMRect } | null>(null);
    const close = useCallback(() => setTip(null), []);
    const allTrigger = useRef<HTMLButtonElement>(null);
    const [allOpen, setAllOpen] = useState(false);
    const closeAll = useCallback(() => setAllOpen(false), []);

    // Grows with the text: no inner scrollbar, so both layers keep the same width.
    useLayoutEffect(() => {
        const el = area.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [value]);

    const segments = useMemo(() => {
        const out: React.ReactNode[] = [];
        let last = 0;
        for (const m of Array.from(value.matchAll(TOKEN_RX))) {
            const at = m.index ?? 0;
            if (at > last) out.push(value.slice(last, at));
            const known = VARIABLE_BY_TOKEN.has(m[0]);
            const empty = known && !values[m[0]];
            out.push(
                // Italic + colour only — never a weight or padding, which would shift the text off the
                // caret. Inter is loaded without italics, so the slant is synthesized at the same widths.
                <span
                    key={at}
                    data-at={at}
                    className={`italic ${
                        !known ? "text-danger" : `text-accent ${empty ? "underline decoration-dotted decoration-accent/60 underline-offset-4" : ""}`
                    } ${tip?.at === at ? "rounded-sm bg-accent-subtle" : ""}`}
                >
                    {m[0]}
                </span>
            );
            last = at + m[0].length;
        }
        out.push(value.slice(last));
        return out;
    }, [value, values, tip]);

    const show = (span: HTMLElement | null) => {
        if (!span) return close();
        const at = Number(span.dataset.at);
        if (tip?.at === at) return;
        setTip({ at, token: span.textContent || "", rect: span.getClientRects()[0] || span.getBoundingClientRect() });
    };

    /** The variable under the pointer: the backdrop ignores the mouse, so its spans are hit-tested. */
    const hover = (e: React.MouseEvent) => {
        const spans: HTMLElement[] = Array.from(backdrop.current?.querySelectorAll<HTMLElement>("[data-at]") || []);
        const hit = spans.find((span) =>
            Array.from(span.getClientRects()).some((r: DOMRect) => e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom)
        );
        show(hit || null);
    };

    /** The variable under the caret, after a click or an arrow key. */
    const pick = () => {
        const el = area.current;
        if (!el) return;
        caret.current = el.selectionStart;
        const pos = el.selectionStart;
        const hit = Array.from(value.matchAll(TOKEN_RX)).find((m) => (m.index ?? 0) < pos && pos < (m.index ?? 0) + m[0].length);
        show(hit ? backdrop.current?.querySelector<HTMLElement>(`[data-at="${hit.index ?? 0}"]`) || null : null);
    };

    /** Inserts a variable where the caret last was, and puts the caret right after it. */
    const insert = (token: string) => {
        const at = Math.min(caret.current, value.length);
        const before = value.slice(0, at);
        const pad = before && !/\s$/.test(before) ? " " : "";
        onChange(before + pad + token + value.slice(at));
        const next = at + pad.length + token.length;
        caret.current = next;
        requestAnimationFrame(() => {
            area.current?.focus();
            area.current?.setSelectionRange(next, next);
        });
    };

    const tipDef = tip && VARIABLE_BY_TOKEN.get(tip.token);
    const available = VARIABLES.filter((v) => examples || values[v.token]);
    const missing = examples ? [] : VARIABLES.filter((v) => !values[v.token] && v.group !== "Vous");
    const missingMine = examples ? [] : VARIABLES.filter((v) => !values[v.token] && v.group === "Vous");
    // The few a message uses most, inline; the rest one click away.
    const quick = available.filter((v) => QUICK.includes(v.token));

    return (
        <div>
            <div
                className={`relative rounded-xl border bg-surface transition-colors ${
                    invalid
                        ? "border-danger ring-2 ring-danger/15"
                        : "border-border hover:border-border-strong focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/10"
                }`}
            >
                <div ref={backdrop} aria-hidden className={`${BOX} pointer-events-none absolute inset-0 overflow-hidden text-ink`}>
                    {/* The textarea's own placeholder would be transparent like its text: drawn here instead */}
                    {value ? segments : <span className="text-ink-faint">{placeholder}</span>}
                    {/* A trailing newline needs a line to sit on, as in the textarea */}
                    {"​"}
                </div>
                {/* bg-[transparent] / text-[transparent], not bg-transparent: tailwind.config replaces the
                    palette without `transparent`, so those classes don't exist and the textarea kept the
                    browser's white background over the coloured layer — the text looked invisible. */}
                <textarea
                    ref={area}
                    value={value}
                    readOnly={readOnly}
                    rows={minRows}
                    aria-placeholder={placeholder}
                    spellCheck={false}
                    onChange={(e) => {
                        onChange(e.target.value);
                        caret.current = e.target.selectionStart;
                        close();
                    }}
                    onClick={pick}
                    onMouseMove={hover}
                    onMouseLeave={close}
                    onKeyUp={(e) => {
                        caret.current = e.currentTarget.selectionStart;
                        if (e.key.startsWith("Arrow")) pick();
                    }}
                    onBlur={(e) => (caret.current = e.currentTarget.selectionStart)}
                    className={`${BOX} relative block w-full resize-none overflow-hidden border-0 bg-[transparent] text-[transparent] caret-ink [-webkit-text-fill-color:transparent] selection:bg-accent/25 focus:outline-none`}
                />
            </div>

            {/* What the variable stands for — just its value, on hover or click */}
            {tip &&
                createPortal(
                    <div
                        role="tooltip"
                        style={{ position: "fixed", left: tip.rect.left, top: tip.rect.top - 6, transform: "translateY(-100%)" }}
                        className="pointer-events-none z-[3000] max-w-xs rounded-lg bg-ink px-2.5 py-1.5 font-sans text-xs text-ink-inverse shadow-float"
                    >
                        <span className="text-ink-inverse/60">{tipDef ? tipDef.label : "Variable inconnue"} · </span>
                        {tipDef
                            ? values[tip.token] || (tipDef.group === "Vous" ? "non renseigné dans Mon compte" : "pas de donnée, retirée à l'envoi")
                            : "restera telle quelle"}
                        {examples && tipDef ? " (exemple)" : ""}
                    </div>,
                    document.body
                )}

            {showList && !readOnly && (
                <div className="mt-2 flex flex-wrap items-center gap-1" onMouseDown={(e) => e.preventDefault()}>
                    <span className="mr-0.5 font-sans text-xs text-ink-faint">Insérer</span>
                    {quick.map((v) => (
                        <button
                            key={v.token}
                            type="button"
                            onClick={() => insert(v.token)}
                            title={`${v.token} · ${values[v.token] || v.example}`}
                            className="rounded-md bg-accent-subtle px-1.5 py-0.5 font-sans text-xs italic text-accent transition-colors hover:bg-accent hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                            {v.label}
                        </button>
                    ))}
                    <button
                        ref={allTrigger}
                        type="button"
                        onClick={() => setAllOpen((o) => !o)}
                        aria-haspopup="menu"
                        aria-expanded={allOpen}
                        className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-sans text-xs text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                        Toutes ({available.length})
                        <ChevronDown className="h-3 w-3" />
                    </button>
                    <FloatingMenu anchor={allTrigger} open={allOpen} onClose={closeAll} align="left" width={320}>
                        <div className="max-h-80 overflow-y-auto">
                            {GROUPS.map((group) => {
                                const list = available.filter((v) => v.group === group);
                                if (!list.length) return null;
                                return (
                                    <div key={group} className="py-1">
                                        <p className="px-3 pb-1 pt-1.5 font-sans text-[11px] font-semibold uppercase tracking-wide text-ink-faint">{group}</p>
                                        {list.map((v) => (
                                            <button
                                                key={v.token}
                                                role="menuitem"
                                                onClick={() => {
                                                    insert(v.token);
                                                    closeAll();
                                                }}
                                                className={`${MENU_ITEM} justify-between !py-1.5`}
                                            >
                                                <span className="shrink-0 italic text-accent">{v.label}</span>
                                                <span className="truncate text-right text-xs text-ink-muted">{values[v.token] || v.example}</span>
                                            </button>
                                        ))}
                                    </div>
                                );
                            })}
                            {(missing.length > 0 || missingMine.length > 0) && (
                                <div className="space-y-1 border-t border-border px-3 py-2 font-sans text-xs text-ink-faint">
                                    {missing.length > 0 && <p>Sans donnée pour ce prospect : {missing.map((v) => v.label.toLowerCase()).join(", ")}.</p>}
                                    {missingMine.length > 0 && (
                                        <p>
                                            À renseigner dans{" "}
                                            <a href="/settings" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-ink">
                                                Mon compte
                                            </a>
                                            {" "}: {missingMine.map((v) => v.label.toLowerCase()).join(", ")}.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </FloatingMenu>
                </div>
            )}
        </div>
    );
}
